import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  Repeat, Plus, Pause, Play, X, RefreshCw, Trash2, ChevronDown, ChevronRight,
} from 'lucide-react';
import {
  appointmentSeries, admin as adminApi, services as servicesApi,
  groomers as groomersApi, pets as petsApi,
} from '../utils/api';

/**
 * SeriesManager — admin UI for roadmap item #19 Phase 1.
 *
 * One mounted panel on the admin dashboard. Lists every series (any
 * status), expands per row to show occurrences, and has a "New series"
 * inline form. Pause/Resume/End buttons mutate the row; "Run now"
 * fires the materializer for the whole shop (useful when there's no
 * cron daemon, e.g. shared hosting).
 *
 * Frequency model is the simplified one the backend exposes: weekly
 * or monthly with an integer interval. "Every 6 weeks" = weekly +
 * interval 6; "every 3 months" = monthly + interval 3.
 */
const SeriesManager = () => {
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [creating, setCreating] = useState(false);

  // Lookups for the create form.
  const [clients, setClients] = useState([]);
  const [allServices, setAllServices] = useState([]);
  const [allGroomers, setAllGroomers] = useState([]);
  const [petsForClient, setPetsForClient] = useState([]);

  const [form, setForm] = useState({
    client_id: '',
    pet_id: '',
    service_id: '',
    groomer_id: '',
    start_date: '',
    start_time: '09:00',
    frequency: 'weekly',
    interval_n: 1,
    until_date: '',
    max_occurrences: '',
    special_requests: '',
  });

  useEffect(() => {
    loadAll();
  }, []);

  // Re-fetch the client's pets whenever the picked client changes. Pets
  // are scoped per-client in the schema so we can't load them upfront.
  useEffect(() => {
    if (!form.client_id) { setPetsForClient([]); return; }
    (async () => {
      try {
        const cd = await adminApi.getClientDetails(form.client_id);
        setPetsForClient(cd.data?.pets || []);
      } catch (_) { setPetsForClient([]); }
    })();
  }, [form.client_id]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [seriesRes, clientsRes, svcRes, gmrRes] = await Promise.all([
        appointmentSeries.list(),
        adminApi.getClients(),
        servicesApi.getAll(),
        groomersApi.getAll(),
      ]);
      setSeries(Array.isArray(seriesRes.data) ? seriesRes.data : []);
      setClients(Array.isArray(clientsRes.data) ? clientsRes.data : []);
      setAllServices(Array.isArray(svcRes.data) ? svcRes.data : []);
      setAllGroomers(Array.isArray(gmrRes.data) ? gmrRes.data : []);
    } catch (e) {
      console.error('Load series:', e);
      toast.error('Could not load series');
    } finally {
      setLoading(false);
    }
  };

  const refreshSeries = async () => {
    try {
      const r = await appointmentSeries.list();
      setSeries(Array.isArray(r.data) ? r.data : []);
    } catch (_) {}
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.client_id || !form.start_date || !form.start_time) {
      return toast.error('Client, start date, and time are required');
    }
    setCreating(true);
    try {
      const payload = {
        ...form,
        interval_n: Number(form.interval_n) || 1,
        max_occurrences: form.max_occurrences === '' ? null : Number(form.max_occurrences),
        until_date: form.until_date || null,
      };
      const r = await appointmentSeries.create(payload);
      const m = r.data?.materialized;
      toast.success(`Series created. ${m?.created || 0} occurrences booked.`);
      setForm({ ...form, pet_id: '', service_id: '', groomer_id: '', special_requests: '' });
      refreshSeries();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not create series');
    } finally {
      setCreating(false);
    }
  };

  const handlePause = async (id) => {
    try { await appointmentSeries.pause(id); toast.success('Series paused'); refreshSeries(); }
    catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
  };
  const handleResume = async (id) => {
    try { const r = await appointmentSeries.resume(id); toast.success(`Resumed — ${r.data?.materialized?.created || 0} new occurrences`); refreshSeries(); }
    catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
  };
  const handleEnd = async (id) => {
    const cancelFuture = window.confirm('End this series.\n\nClick OK to ALSO cancel all future appointments already on the calendar.\nClick Cancel to leave them as-is (the series just stops generating new ones).');
    try {
      const r = await appointmentSeries.end(id, cancelFuture);
      toast.success(cancelFuture ? `Series ended. ${r.data?.future_cancelled || 0} future appts cancelled.` : 'Series ended.');
      refreshSeries();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
  };
  const handleMaterializeOne = async (id) => {
    try { const r = await appointmentSeries.materializeOne(id); toast.success(`Created ${r.data?.created || 0} occurrences`); refreshSeries(); }
    catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
  };
  const handleRunAll = async () => {
    try { const r = await appointmentSeries.runAll(); toast.success(`Run complete: ${r.data?.created || 0} occurrences across ${r.data?.series_processed || 0} series`); refreshSeries(); }
    catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
  };

  const cadenceLabel = (s) => {
    const i = Number(s.interval_n) || 1;
    if (s.frequency === 'monthly') return i === 1 ? 'Every month' : `Every ${i} months`;
    return i === 1 ? 'Every week' : `Every ${i} weeks`;
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <Repeat className="text-primary-600" size={22} />
          Recurring Appointments
        </h2>
        <button onClick={handleRunAll} className="btn btn-secondary flex items-center gap-2 text-sm" title="Materialize all active series now">
          <RefreshCw size={14} /> Run materializer
        </button>
      </div>
      <p className="text-sm text-gray-600 mb-4">
        Series automatically generate appointments on a rolling 60-day window. Occurrences that
        fall on closures or conflicting slots are skipped and logged; the series keeps going.
      </p>

      {/* ── Create form ──────────────────────────────────────────── */}
      <form onSubmit={handleCreate} className="border rounded p-4 mb-6 bg-gray-50 space-y-3">
        <div className="font-semibold flex items-center gap-2 text-gray-800">
          <Plus size={16} /> New series
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Client *</label>
            <select className="input" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value, pet_id: '' })}>
              <option value="">— Choose —</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Pet</label>
            <select className="input" value={form.pet_id} onChange={(e) => setForm({ ...form, pet_id: e.target.value })}>
              <option value="">— None —</option>
              {petsForClient.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Service</label>
            <select className="input" value={form.service_id} onChange={(e) => setForm({ ...form, service_id: e.target.value })}>
              <option value="">— Default flat rate —</option>
              {allServices.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Groomer</label>
            <select className="input" value={form.groomer_id} onChange={(e) => setForm({ ...form, groomer_id: e.target.value })}>
              <option value="">— Any —</option>
              {allGroomers.map((g) => (
                <option key={g.id} value={g.id}>{g.first_name} {g.last_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Start date *</label>
            <input type="date" className="input" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Start time *</label>
            <input type="time" className="input" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Frequency</label>
            <select className="input" value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })}>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Every N {form.frequency === 'monthly' ? 'months' : 'weeks'}</label>
            <input type="number" min="1" className="input" value={form.interval_n} onChange={(e) => setForm({ ...form, interval_n: e.target.value })} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Until (optional)</label>
            <input type="date" className="input" value={form.until_date} onChange={(e) => setForm({ ...form, until_date: e.target.value })} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Max occurrences (optional)</label>
            <input type="number" min="1" className="input" placeholder="e.g. 12" value={form.max_occurrences} onChange={(e) => setForm({ ...form, max_occurrences: e.target.value })} />
          </div>
          <div className="md:col-span-3">
            <label className="block text-xs font-medium text-gray-700 mb-1">Special requests</label>
            <input className="input" value={form.special_requests} onChange={(e) => setForm({ ...form, special_requests: e.target.value })} placeholder="Bath only, nail trim, etc." />
          </div>
        </div>
        <button type="submit" disabled={creating} className="btn btn-primary flex items-center gap-2">
          <Plus size={16} /> {creating ? 'Creating…' : 'Create series'}
        </button>
      </form>

      {/* ── List ─────────────────────────────────────────────────── */}
      {loading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : series.length === 0 ? (
        <div className="text-sm text-gray-500 italic">No recurring series yet.</div>
      ) : (
        <div className="space-y-2">
          {series.map((s) => (
            <SeriesRow
              key={s.id}
              series={s}
              expanded={expandedId === s.id}
              onToggle={() => setExpandedId(expandedId === s.id ? null : s.id)}
              cadenceLabel={cadenceLabel(s)}
              onPause={() => handlePause(s.id)}
              onResume={() => handleResume(s.id)}
              onEnd={() => handleEnd(s.id)}
              onMaterialize={() => handleMaterializeOne(s.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ── Single-row display, lazily fetches occurrences on expand ───────
const SeriesRow = ({ series, expanded, onToggle, cadenceLabel, onPause, onResume, onEnd, onMaterialize }) => {
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!expanded || detail) return;
    (async () => {
      try {
        const r = await appointmentSeries.get(series.id);
        setDetail(r.data);
      } catch (_) { setDetail({ occurrences: [] }); }
    })();
  }, [expanded, detail, series.id]);

  const statusBadge = {
    active:  'bg-green-100 text-green-800',
    paused:  'bg-yellow-100 text-yellow-800',
    ended:   'bg-gray-200 text-gray-700',
  }[series.status] || 'bg-gray-200 text-gray-700';

  return (
    <div className="border rounded">
      <div className="p-3 flex items-center justify-between gap-3 flex-wrap">
        <button type="button" onClick={onToggle} className="flex items-center gap-2 text-left flex-1 min-w-0">
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <div className="min-w-0">
            <div className="font-medium truncate">
              {series.client_first_name} {series.client_last_name}
              {series.pet_name ? ` — ${series.pet_name}` : ''}
            </div>
            <div className="text-xs text-gray-600 truncate">
              {cadenceLabel} · {series.start_time} · starts {series.start_date}
              {series.until_date ? ` · until ${series.until_date}` : ''}
              {series.service_name ? ` · ${series.service_name}` : ''}
              {series.groomer_first_name ? ` · ${series.groomer_first_name} ${series.groomer_last_name}` : ''}
            </div>
          </div>
        </button>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusBadge}`}>{series.status}</span>
          <span className="text-xs text-gray-500">{series.occurrence_count || 0} occurrences</span>
          {series.status === 'active' && (
            <button onClick={onPause} className="btn btn-secondary text-xs flex items-center gap-1" title="Pause">
              <Pause size={12} /> Pause
            </button>
          )}
          {series.status === 'paused' && (
            <button onClick={onResume} className="btn btn-secondary text-xs flex items-center gap-1" title="Resume">
              <Play size={12} /> Resume
            </button>
          )}
          {series.status === 'active' && (
            <button onClick={onMaterialize} className="btn btn-secondary text-xs flex items-center gap-1" title="Materialize now">
              <RefreshCw size={12} /> Run
            </button>
          )}
          {series.status !== 'ended' && (
            <button onClick={onEnd} className="btn btn-danger text-xs flex items-center gap-1" title="End series">
              <Trash2 size={12} /> End
            </button>
          )}
        </div>
      </div>

      {expanded && (
        <div className="border-t bg-gray-50 p-3">
          {detail === null ? (
            <div className="text-sm text-gray-500">Loading occurrences…</div>
          ) : (detail.occurrences || []).length === 0 ? (
            <div className="text-sm text-gray-500 italic">No occurrences yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-gray-600">
                  <tr>
                    <th className="py-1 pr-3">Date</th>
                    <th className="py-1 pr-3">Time</th>
                    <th className="py-1 pr-3">Status</th>
                    <th className="py-1 pr-3">Payment</th>
                    <th className="py-1 pr-3">Price</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.occurrences.map((o) => (
                    <tr key={o.id} className="border-t border-gray-200">
                      <td className="py-1 pr-3">{o.appointment_date}</td>
                      <td className="py-1 pr-3">{o.start_time}</td>
                      <td className="py-1 pr-3">{o.status}</td>
                      <td className="py-1 pr-3">{o.payment_status}</td>
                      <td className="py-1 pr-3">${Number(o.total_price || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SeriesManager;
